import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useEmblaCarousel from "embla-carousel-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { media } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const ZoomSlide = ({ p, alt, near, onZoom }) => {
  const [zoomed, setZoomed] = useState(false);
  return (
    <TransformWrapper minScale={1} maxScale={5} doubleClick={{ mode: "toggle", step: 0.7 }} wheel={{ step: 0.2 }} pinch={{ step: 5 }}
      panning={{ disabled: !zoomed }} limitToBounds centerZoomedOut
      onTransformed={(_, s) => { const z = s.scale > 1.02; setZoomed(z); onZoom(z); }}>
      <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full">
        {near && <img src={media(p.id)} alt={alt} draggable={false} decoding="async" className="w-full h-full object-contain select-none" />}
      </TransformComponent>
    </TransformWrapper>
  );
};

const useLockScroll = () => {
  useEffect(() => {
    const h = document.documentElement, b = document.body;
    const prev = [h.style.overflow, b.style.overflow, b.style.touchAction];
    h.style.overflow = b.style.overflow = "hidden";
    b.style.touchAction = "none";
    return () => { [h.style.overflow, b.style.overflow, b.style.touchAction] = prev; };
  }, []);
};

export const Lightbox = ({ photos, start = 0, title, onClose }) => {
  const { t } = useLang();
  const zoomed = useRef(false);
  const closeRef = useRef(null);
  const n = photos.length;
  const [ref, embla] = useEmblaCarousel({ direction: "rtl", loop: n > 1, startIndex: start, duration: 22,
    watchDrag: (_, e) => !zoomed.current && !(e.touches && e.touches.length > 1) });
  const [idx, setIdx] = useState(start);
  const [z, setZ] = useState(false);
  useLockScroll();

  const prev = useCallback(() => embla?.scrollPrev(), [embla]);
  const next = useCallback(() => embla?.scrollNext(), [embla]);
  const setZoom = useCallback((z) => { zoomed.current = z; setZ(z); }, []);

  useEffect(() => {
    if (!embla) return undefined;
    const on = () => { setIdx(embla.selectedScrollSnap()); setZoom(false); };
    embla.on("select", on);
    return () => { embla.off("select", on); };
  }, [embla, setZoom]);

  useEffect(() => {
    closeRef.current?.focus();
    const key = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") next();
      else if (e.key === "ArrowRight") prev();
    };
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("keydown", key); };
  }, [onClose, next, prev]);

  const near = (i) => Math.min(Math.abs(i - idx), n - Math.abs(i - idx)) <= 1;
  const btn = "absolute top-1/2 -translate-y-1/2 hidden md:grid h-12 w-12 place-items-center bg-black/60 backdrop-blur border border-white/15 hover:border-white/50 transition-colors";

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-[200] bg-black text-white select-none" style={{ touchAction: "none" }} data-testid="photo-lightbox" data-zoomed={z}>
      <div ref={ref} className="h-full overflow-hidden" dir="rtl">
        <div className="flex h-full">
          {photos.map((p, i) => (
            <div key={i === idx ? `${p.id}-on` : p.id} className="min-w-0 flex-[0_0_100%] h-full" data-testid={`lightbox-slide-${i}`}>
              <ZoomSlide p={p} alt={`${title} ${i + 1}`} near={near(i)} onZoom={i === idx ? setZoom : () => {}} />
            </div>
          ))}
        </div>
      </div>
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-3 md:p-5 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
        <span className="ltr-num text-sm bg-black/60 px-3 py-1" data-testid="lightbox-counter">{idx + 1} / {n}</span>
        <button ref={closeRef} onClick={onClose} aria-label={t("a11y.close")} data-testid="lightbox-close"
          className="pointer-events-auto h-12 w-12 grid place-items-center bg-black/60 border border-white/20 hover:border-white/60 transition-colors">
          <X size={24} />
        </button>
      </div>
      {n > 1 && (
        <>
          <button onClick={prev} aria-label={t("a11y.prev")} className={`${btn} start-4`} data-testid="lightbox-prev"><ChevronRight size={22} /></button>
          <button onClick={next} aria-label={t("a11y.next")} className={`${btn} end-4`} data-testid="lightbox-next"><ChevronLeft size={22} /></button>
        </>
      )}
    </div>,
    document.body,
  );
};
